import { DeviceTypeEditorActions, useEditDeviceTypeStore } from '@shared/hooks/state/EditDeviceTypeContainer';
import DeviceTypeModel from '@shared/models/devices/DeviceTypeModel';
import OrganizationDeviceTypeModel from '@shared/models/organizations/OrganizationDeviceTypeModel';
import IResult from '@shared/models/result/IResult';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAlertsStore } from './state/AlertsContainer';
import { useOrganizationStore } from './state/OrganizationContainer';
import useResolveCurrentOrg from './useResolveCurrentOrg';

export function useCurrentOrgDeviceTypeEditor(initialModel?: DeviceTypeModel | OrganizationDeviceTypeModel | null) {
    const { t } = useTranslation();
    const { organizationId } = useResolveCurrentOrg();
    const { deviceId } = useParams();
    const { updateOrgDeviceType, createOrgDeviceType } = useOrganizationStore.getState();
    const { handleInputChange } = useEditDeviceTypeStore.getState();
    const editDeviceType = useEditDeviceTypeStore((state) => state.editDeviceType);

    useEffect(() => {
        const editDeviceTypeStore = useEditDeviceTypeStore.getState();
        if (
            (deviceId && deviceId == editDeviceTypeStore.editDeviceType?.id && !editDeviceTypeStore.hasChanges) ||
            !initialModel
        ) {
            return;
        }
        editDeviceTypeStore.initializeEditor(initialModel, organizationId);
        editDeviceTypeStore.initializeEditListing(organizationId, deviceId);
        editDeviceTypeStore.initializeEditCustomControls(organizationId, deviceId);
    }, [deviceId, organizationId, initialModel]);

    const actions: DeviceTypeEditorActions = {
        updateOrgDeviceType,
        createOrgDeviceType,
    };

    const handleAlerts = (result: IResult<DeviceTypeModel>) => {
        if (result.resultType === 'Ok') {
            useAlertsStore.getState().showSuccess(t('common:saved'));
        } else {
            useAlertsStore.getState().showError(t('common:saveError'));
        }
    };

    const handleSave = async () => {
        if (!organizationId) throw new Error('Missing organization ID');
        return await useEditDeviceTypeStore.getState().handleSave(actions, organizationId, deviceId);
    };

    const handleSaveWithAlerts = async () => {
        const result = await handleSave();
        handleAlerts(result);
        return result;
    };

    const handleCreateAndClearEditor = async () => {
        if (!organizationId) throw new Error('Missing organization ID');
        const result = await useEditDeviceTypeStore.getState().handleCreateAndClearEditor(actions, organizationId);
        handleAlerts(result);
        return result;
    };

    const resetEditor = () => {
        useEditDeviceTypeStore.getState().initializeEditor(null, organizationId);
    };

    return {
        handleSave,
        handleSaveWithAlerts,
        handleCreateAndClearEditor,
        handleInputChange: handleInputChange,
        editDeviceType: editDeviceType,
        resetEditor,
    };
}
